import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectAdminToken } from "../../admin/sessionSlice";
import { setBackButtonUrl } from "../uiSlice";
import { apiUrl } from "../../utils/apiUrl";
import { parseTimeToSeconds, formatMmSs } from "../../utils/yogaPoints";
import { themeButtonBackground, themeButtonTextColor } from "../../utils/colorUtils";
import "./travelLeaderboard.css";

function sortRows(rows) {
  return [...rows]
    .sort((a, b) => {
      const sb = Number(b.score ?? b.points ?? 0);
      const sa = Number(a.score ?? a.points ?? 0);
      if (sb !== sa) return sb - sa;
      return parseTimeToSeconds(a.time) - parseTimeToSeconds(b.time);
    })
    .map((r, i) => ({ ...r, rank: i + 1 }));
}

export default function TravelLeaderboard() {
  const dispatch = useDispatch();
  const { data: themeData } = useSelector((s) => s.theme);
  const user = useSelector((s) => s.auth.user);
  const adminToken = useSelector(selectAdminToken);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const headBg = themeButtonBackground(themeData) || "#e05c3a";
  const headText = themeButtonTextColor(themeData) || "#ffffff";
  const titleColor = themeData?.text_color || "#1a2b6d";

  useEffect(() => {
    dispatch(setBackButtonUrl(user?.backButtonRedirect || "/login?&save=true"));
  }, [dispatch, user?.backButtonRedirect]);

  useEffect(() => {
    if (!user || !adminToken) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const resp = await fetch(apiUrl("/gameMatchUsers"), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({
            userId: user.userId || user.id,
            sessionId: user.sessionId,
            organizationId: user.organizationId,
          }),
        });
        const data = await resp.json().catch(() => ({}));
        if (cancelled) return;
        if (!resp.ok || !data.success) {
          setError(data.error || data.message || "Could not load leaderboard");
          setRows([]);
          return;
        }
        setRows(Array.isArray(data.matches) ? data.matches : []);
      } catch (e) {
        if (!cancelled) {
          setError(e.message || "Network error");
          setRows([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, adminToken]);

  const sorted = useMemo(() => sortRows(rows), [rows]);

  const myId = String(user?.userId || user?.id || "");
  const me = useMemo(
    () =>
      sorted.find((r) => String(r.userId || "") === myId) ||
      sorted.find((r) => r.isSelf) ||
      null,
    [sorted, myId]
  );

  // Top 5, plus the current user as a 6th row when they're outside the top 5.
  const displayRows = useMemo(() => {
    const top = sorted.slice(0, 5);
    if (me && me.rank > 5 && !top.some((r) => r.rank === me.rank)) {
      return [...top, me];
    }
    return top;
  }, [sorted, me]);

  return (
    <div className="tlb-page">
      <h1 className="tlb-title" style={{ color: titleColor }}>
        Leaderboard
      </h1>

      {loading ? (
        <div className="quiz-loader-container">
          <div className="quiz-loader" />
        </div>
      ) : error ? (
        <p className="tlb-msg">{error}</p>
      ) : displayRows.length === 0 ? (
        <p className="tlb-msg">No scores yet.</p>
      ) : (
        <div className="tlb-table-wrap">
          <table className="tlb-table">
            <thead>
              <tr style={{ backgroundColor: headBg, color: headText }}>
                <th>Rank</th>
                <th>Name</th>
                <th>Points</th>
                <th>Time</th>
              </tr>
            </thead>
            <tbody>
              {displayRows.map((r) => {
                const isMe = String(r.userId || "") === myId || r.isSelf;
                return (
                  <tr key={r.userId || `rank-${r.rank}`} className={isMe ? "tlb-row--you" : undefined}>
                    <td>{r.rank}</td>
                    <td>
                      {r.name || "Anonymous"}
                      {isMe ? " (You)" : ""}
                    </td>
                    <td>{Number(r.score ?? r.points ?? 0)}</td>
                    <td>{formatMmSs(parseTimeToSeconds(r.time))}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
